import React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { change, FieldArray, formValueSelector } from 'redux-form'
// components
import { ReduxDatePicker, ReduxInput, ReduxSelect } from 'components/FormElements/ReduxForm'
import renderReviewers from 'components/Modals/CreateScheduledReviewModal/components/Form/components/RenderReviewers'
// view components
import RecurrenceEnds from 'components/Modals/KPIRecurrenceModal/Form/components/RecurrenceEnds'
import FirstReviewEndsDate from 'components/Modals/CreateScheduledReviewModal/components/Form/components/RepeatingReviewForm/components/FirstReviewEndsDate'
// views
import InputLabel from 'components/FormElements/components/InputLabel'
// constants
import {
  EACH_PERIOD_ENDS_TYPE_OPTIONS,
  EACH_PERIOD_ENDS_TYPE_VALUES,
  Forms,
  REPEAT_OPTIONS,
  REPEAT_VALUES
} from 'constants/index'
// utils
import moment from 'moment'
import { atLeast, getValueFromOption, greaterThanZero, required } from 'utils/index'

import styled from 'styled-components'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Container = styled.div`
  display: flex;
  gap: 16px;

  > div {
    flex: 1;
  }
`

interface IIncomingProps {
  form?: string
  hideReviewers?: boolean
  chosenReviewersIds?: string[]
}

const RepeatingReviewForm: React.FC<IIncomingProps> = ({
  form = Forms.SCHEDULE_REVIEW_FORM,
  hideReviewers,
  chosenReviewersIds
}) => {
  const selector = formValueSelector(form)

  const { repeatEveryValue, repeatEveryType, periodEndsOn, startPeriod } = useSelector((state) => ({
    repeatEveryValue: selector(state, `repeatEveryValue`),
    repeatEveryType: getValueFromOption(selector(state, 'repeatEveryType')) || '',
    periodEndsOn: selector(state, `periodEndsOn`),
    startPeriod: selector(state, `startPeriod`)
  }))

  const dispatch = useDispatch()

  const changeField = (field: string, value: any) => dispatch(change(form, field, value))

  const recalculateDates = () => {
    const startPoint = moment(startPeriod)
    const repeatEvery = parseInt(repeatEveryValue)

    if (!startPeriod) {
      changeField(`startPeriod`, moment())
      return
    }

    if (repeatEveryType === REPEAT_VALUES.WEEKLY) {
      let endPeriod

      if (getValueFromOption(periodEndsOn) === EACH_PERIOD_ENDS_TYPE_VALUES.THE_SAME_DAY) {
        endPeriod = startPoint.clone().add(repeatEveryValue, 'week')
      }
      if (getValueFromOption(periodEndsOn) === EACH_PERIOD_ENDS_TYPE_VALUES.LAST_DAY) {
        endPeriod = startPoint
          .clone()
          .add(atLeast(0)(repeatEvery - 1), 'week')
          .endOf('isoWeek')
      }

      changeField(`firstPeriodEndsAt`, endPeriod)
    }

    if (repeatEveryType === REPEAT_VALUES.MONTHLY) {
      let endPeriod

      if (getValueFromOption(periodEndsOn) === EACH_PERIOD_ENDS_TYPE_VALUES.THE_SAME_DAY) {
        endPeriod = startPoint.clone().add(repeatEveryValue, 'month').subtract(1, 'day')
      }
      if (getValueFromOption(periodEndsOn) === EACH_PERIOD_ENDS_TYPE_VALUES.LAST_DAY) {
        endPeriod = startPoint
          .clone()
          .add(atLeast(0)(repeatEvery - 1), 'month')
          .endOf('month')
      }
      changeField(`firstPeriodEndsAt`, endPeriod)
    }
  }

  React.useEffect(recalculateDates, [repeatEveryValue, repeatEveryType, startPeriod, periodEndsOn])

  return (
    <Wrapper>
      <Container>
        <ReduxInput name={'repeatEveryValue'} label={'Repeat'} type={'number'} validate={[greaterThanZero, required]} />
        <ReduxSelect name={'repeatEveryType'} label={'Every'} options={REPEAT_OPTIONS} validate={[required]} />
      </Container>
      <ReduxSelect
        label={'Each period ends on'}
        name={'periodEndsOn'}
        options={EACH_PERIOD_ENDS_TYPE_OPTIONS}
        validate={[required]}
      />
      <Container>
        <ReduxDatePicker label={'Start of reporting period'} name={'startPeriod'} />
        <ReduxDatePicker label={'End of reporting period'} name={'firstPeriodEndsAt'} disabled />
      </Container>

      {!hideReviewers && (
        <>
          <InputLabel>Specify reviewers and expected completion dates</InputLabel>
          <FieldArray
            name={'repeatingReviewReviewers'}
            component={renderReviewers as any}
            chosenReviewersIds={chosenReviewersIds}
            isRepeatingReview
          />
        </>
      )}
      <InputLabel>Expected completion date</InputLabel>
      <FirstReviewEndsDate form={form} />
      <RecurrenceEnds form={form} />
    </Wrapper>
  )
}

export default RepeatingReviewForm
